import { useEffect, useState } from "react";
import {
  ApiError,
  listSectors,
  readApplication,
  readDurationGuardrails,
  type CertificationBody,
  type DurationGuardrails,
  type Sector,
} from "../api/client";
import { useBrandMark } from "./useBrandMark";

/**
 * Screen 22 — the organisation profile.
 *
 * Read-only, and that is the whole design. Every value here was answered during onboarding
 * and assessed by a Complao administrator under Cl. 5.2, so the screen shows the record
 * rather than a form over it: the accredited scope card says in as many words that changing
 * it requires re-verification. There is no edit route at M2-1 and adding one would let a
 * body widen its own accredited scope after the assessment that approved the narrower one.
 *
 * It reads `GET /onboarding/certification-bodies` — the same reader the administrator's
 * verification screen uses. One reader means what a body sees of itself and what an
 * assessor sees of it cannot come to disagree.
 */
export function OrganisationProfile({ tenantId }: { tenantId: string }) {
  const [record, setRecord] = useState<CertificationBody | null>(null);
  const mark = useBrandMark(tenantId);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [guardrails, setGuardrails] = useState<DurationGuardrails | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    readApplication()
      .then(setRecord)
      .catch((cause: unknown) =>
        setError(cause instanceof ApiError ? cause.message : "The profile could not be read."),
      );
    listSectors().then(setSectors).catch(() => undefined);
    readDurationGuardrails().then(setGuardrails).catch(() => undefined);
  }, []);

  if (error) {
    return (
      <p className="problem" role="alert">
        {error}
      </p>
    );
  }
  if (record === null) return <p className="sub">Reading the profile&hellip;</p>;

  return (
    <>
      <div className="between" style={{ marginBottom: "16px" }}>
        <div>
          <div className="eyebrow">Certification body</div>
          <h1>Organisation profile</h1>
          <p className="sub">Captured at onboarding and verified by Complao</p>
        </div>
        <div style={{ textAlign: "right" }}>
          <Standing record={record} />
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <h2>Legal identity</h2>
          <dl className="kv">
            <dt>Legal entity</dt>
            <dd>{value(record.legal_name)}</dd>
            <dt>Trading as</dt>
            <dd>{value(record.trading_name)}</dd>
            <dt>Registration</dt>
            <dd>
              <span className="mono">{value(record.registration_number)}</span>
            </dd>
            <dt>Country</dt>
            <dd>{value(record.country)}</dd>
            <dt>Address</dt>
            <dd>{value(record.address)}</dd>
            <dt>Mark</dt>
            <dd>
              {mark ? (
                <img
                  className="brandmark"
                  src={mark}
                  alt={`${record.legal_name ?? "Certification body"} mark`}
                />
              ) : (
                <span className="sub">None uploaded</span>
              )}
            </dd>
          </dl>
        </div>

        <div className="card">
          <h2>Accreditation</h2>
          <dl className="kv">
            <dt>Body</dt>
            <dd>
              {value(record.accreditation_body)}
              {record.accreditation_number ? (
                <>
                  {" — "}
                  <span className="mono">{record.accreditation_number}</span>
                </>
              ) : null}
            </dd>
            <dt>Status</dt>
            <dd>
              <AccreditationStatus status={record.accreditation_status ?? null} />
            </dd>
            <dt>Expires</dt>
            <dd>{value(record.accreditation_expiry)}</dd>
            <dt>Next assessment</dt>
            <dd>{value(record.next_assessment)}</dd>
            <dt>IAF MLA</dt>
            <dd>{record.mla_signatory ? "Signatory" : "Not a signatory"}</dd>
          </dl>
        </div>
      </div>

      <div className="card">
        <h2>Accredited scope</h2>
        <p className="sub" style={{ marginBottom: "12px" }}>
          Audits can only be configured within this matrix. Changing it requires
          re-verification by Complao.
        </p>
        <div className="tscroll">
          <table>
            <thead>
              <tr>
                <th>Standard</th>
                <th>Accredited sectors</th>
                <th>Enabled in Complao</th>
              </tr>
            </thead>
            <tbody>
              {(record.scopes ?? []).length === 0 ? (
                <tr>
                  <td colSpan={3}>No accredited scope has been recorded.</td>
                </tr>
              ) : (
                (record.scopes ?? []).map((scope) => (
                  <tr key={scope.standard}>
                    <td>
                      <b>{scope.standard}</b>
                    </td>
                    <td>
                      {(scope.sectors ?? []).map((index) => (
                        <div key={index} className="sub">
                          {sectorLabel(sectors, index)}
                        </div>
                      ))}
                    </td>
                    <td>
                      {(record.enabled_standards ?? []).includes(scope.standard) ? (
                        <span className="pill p-ok">Enabled</span>
                      ) : (
                        <span className="pill p-mute">Not enabled</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <h2>Impartiality and liability</h2>
          <dl className="kv">
            <dt>Impartiality committee</dt>
            <dd>
              {record.impartiality_committee
                ? "Yes — Cl. 5.2 satisfied"
                : "No — recorded, and read at verification"}
            </dd>
            <dt>Consultancy offered</dt>
            <dd>{record.consultancy_offered ? "Yes — Cl. 4.2 declared" : "No"}</dd>
            <dt>Liability</dt>
            <dd>{value(record.liability_cover)}</dd>
          </dl>
        </div>

        <div className="card">
          <h2>People and configuration</h2>
          <dl className="kv">
            <dt>Administrator</dt>
            <dd>{value(record.administrator)}</dd>
            <dt>Decision maker</dt>
            <dd>
              {value(record.decision_maker)}{" "}
              <span className="sub">— Cl. 9.5, must not audit</span>
            </dd>
            <dt>Technical manager</dt>
            <dd>{value(record.technical_manager)}</dd>
            <dt>Reference prefix</dt>
            <dd>
              <span className="mono">{value(record.reference_prefix)}</span>
            </dd>
            <dt>Time zone</dt>
            <dd>{value(record.timezone)}</dd>
            <dt>Duration threshold</dt>
            <dd>
              {record.duration_threshold}%{" "}
              <DurationThresholdNote
                threshold={record.duration_threshold}
                guardrails={guardrails}
              />
            </dd>
            <dt>Stage 1 share</dt>
            <dd>
              {record.stage1_percentage}%{" "}
              <span className="sub">— CB procedure, not an MD 5 formula</span>
            </dd>
            <dt>Retention</dt>
            <dd>{value(record.retention_policy)}</dd>
          </dl>
        </div>
      </div>
    </>
  );
}

/**
 * The two decisions, kept apart on the screen as they are in the database.
 *
 * Verification is the Cl. 5.2 assessment of the accreditation and it is what this pill
 * reports. Activation — whether the body may sign in — is a different column and is not
 * shown here, because a body reading this screen has already signed in and a second green
 * pill saying so would read as a second assessment.
 */
function Standing({ record }: { record: CertificationBody }) {
  if (record.verified_at) {
    return (
      <>
        <span className="pill p-ok">Verified</span>
        <div className="sub" style={{ marginTop: "5px" }}>
          {record.verified_by ?? "—"} · {record.verified_at.slice(0, 10)}
        </div>
      </>
    );
  }
  if (record.onboarding_completed_at) {
    return (
      <>
        <span className="pill p-wait">Awaiting verification</span>
        <div className="sub" style={{ marginTop: "5px" }}>
          Submitted {record.onboarding_completed_at.slice(0, 10)}
        </div>
      </>
    );
  }
  return <span className="pill p-mute">Not yet submitted</span>;
}

/** The registry's own words, in the prototype's pill. A suspension is not decoration. */
function AccreditationStatus({ status }: { status: string | null }) {
  if (status === null) return <>—</>;
  return status.toLowerCase() === "active" ? (
    <span className="pill p-ok">{status}</span>
  ) : (
    <span className="pill p-red">{status}</span>
  );
}

/**
 * The number the prototype gets wrong.
 *
 * Screen 22 prints "stricter than the platform default of 75%". The floor is **80** — MD 5
 * Cl. 2.1.2 — and the prototype's 75 is the same error its field, help text and validator
 * carry (open item 3). The comparison is made against the `durationFloor` guardrail the
 * server serves, so the browser, the validator, the engine and the CHECK constraint stay
 * one number rather than four copies of it. With the guardrail unread, no comparison is
 * printed at all: a wrong sentence about a regulatory floor is worse than a missing one.
 */
function DurationThresholdNote({
  threshold,
  guardrails,
}: {
  threshold: number;
  guardrails: DurationGuardrails | null;
}) {
  if (guardrails === null) return null;
  const floor = guardrails.threshold_floor_percent;
  return (
    <span className="sub">
      {threshold > floor
        ? `— stricter than the MD 5 Cl. 2.1.2 floor of ${floor}%`
        : `— the MD 5 Cl. 2.1.2 floor`}
    </span>
  );
}

/** "IAF 17 · Basic metals…", from the registry rather than a constant in the browser. */
function sectorLabel(sectors: Sector[], index: number): string {
  const match = sectors.find((sector) => sector.index === index);
  return match ? `IAF ${String(index).padStart(2, "0")} · ${match.label}` : `IAF ${index}`;
}

function value(text: string | null | undefined): string {
  return text && text.trim() ? text : "—";
}
